(function (global) {
  'use strict';

  function readPurchaseState(product, sectionId) {
    const form = product.querySelector(`#product-form-${global.CSS.escape(sectionId)}`);
    const submit = form?.querySelector('[name="add"][type="submit"]');
    const owner = form?.closest('product-form');
    const variant = form?.elements.namedItem('id');
    const quantity = form?.elements.namedItem('quantity');
    const selector = product.querySelector('variant-selects');
    let selection = null;
    if (selector) {
      try {
        selection = JSON.parse(selector.querySelector('[data-selected-variant]')?.textContent || 'null');
      } catch {
        selection = null;
      }
    }
    const ready = Boolean(form && submit && owner && typeof owner.onSubmitHandler === 'function');
    const selectionMatches = !selector || (selection && String(selection.id) === variant?.value);
    const loading = Boolean(submit?.classList.contains('loading'));
    const blocked = !ready || !variant || variant.disabled || !/^\d+$/.test(variant.value) ||
      !selectionMatches || Boolean(product.pendingRequestUrl) || Boolean(submit?.disabled) ||
      submit?.getAttribute('aria-disabled') === 'true' || loading;
    const label = submit?.querySelector('span:not(.loading__spinner)')?.textContent.trim() || '';
    const price = product.querySelector(`#price-${global.CSS.escape(sectionId)}`);
    return { form, submit, owner, variant, quantity, selection, ready, blocked, loading, label, price };
  }

  function intersects(a, b) {
    return a.bottom > b.top && a.top < b.bottom && a.right > b.left && a.left < b.right;
  }

  class DinkStickyPurchase extends global.HTMLElement {
    connectedCallback() {
      this.dispose?.();
      this.hidden = true;
      this.product = this.closest('product-info');
      this.button = this.querySelector('[data-cdp-submit]');
      this.label = this.querySelector('[data-cdp-submit-label]');
      this.selection = this.querySelector('[data-cdp-selection]');
      this.price = this.querySelector('[data-cdp-price]');
      this.edit = this.querySelector('[data-cdp-edit]');
      if (!this.product || !this.button || !this.label || !this.price || !this.edit || !this.selection) return;
      this.cleanups = [];
      const listen = (target, event, callback, options) => {
        target.addEventListener(event, callback, options);
        this.cleanups.push(() => target.removeEventListener(event, callback, options));
      };
      this.schedule = () => {
        if (this.frame != null) return;
        this.frame = global.requestAnimationFrame(() => {
          this.frame = null;
          if (this.isConnected) this.sync();
        });
      };
      listen(this.button, 'click', this.activate.bind(this));
      listen(this.edit, 'click', (event) => {
        event.preventDefault();
        this.editNative();
      });
      listen(this.product, 'change', this.schedule);
      listen(this.product, 'input', this.schedule);
      listen(this.product, 'product-info:loaded', this.schedule);
      listen(global, 'scroll', this.schedule, { passive: true });
      listen(global, 'resize', this.schedule, { passive: true });
      listen(global.document, 'focusin', this.schedule);
      listen(global.document, 'toggle', this.schedule, true);
      listen(global.document, 'transitionend', this.schedule, true);
      listen(global.document, 'shopify:section:unload', (event) => {
        if (event.target.contains(this)) this.dispose();
      });
      if (global.visualViewport) {
        listen(global.visualViewport, 'resize', this.schedule, { passive: true });
        listen(global.visualViewport, 'scroll', this.schedule, { passive: true });
      }
      this.mutations = new global.MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target) && record.target !== global.document.documentElement)) {
          this.schedule();
        }
      });
      this.mutations.observe(global.document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        characterData: true,
        attributeFilter: ['class', 'style', 'disabled', 'aria-disabled', 'aria-hidden', 'hidden', 'open', 'value'],
      });
      if (global.ResizeObserver) {
        this.resizeObserver = new global.ResizeObserver(this.schedule);
        this.resizeObserver.observe(this);
        this.resizeObserver.observe(this.product);
        global.document.querySelectorAll('.section-header').forEach((header) => this.resizeObserver.observe(header));
      }
      this.dispose = () => {
        this.cleanups.splice(0).forEach((cleanup) => cleanup());
        this.mutations.disconnect();
        this.resizeObserver?.disconnect();
        if (this.frame != null) global.cancelAnimationFrame(this.frame);
        this.frame = null;
        this.setVisible(false);
      };
      this.sync();
    }

    disconnectedCallback() {
      this.dispose?.();
    }

    visible(element) {
      if (!element || !element.getClientRects().length) return false;
      for (let ancestor = element.parentElement; ancestor; ancestor = ancestor.parentElement) {
        if (ancestor.tagName !== 'DETAILS' || ancestor.open) continue;
        const summary = Array.from(ancestor.children).find((child) => child.tagName === 'SUMMARY');
        if (!summary?.contains(element)) return false;
      }
      const style = global.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse';
    }

    headerBottom() {
      let bottom = 0;
      global.document.querySelectorAll('.section-header, [data-cdp-top-obstruction]').forEach((header) => {
        if (!this.visible(header)) return;
        const rect = header.getBoundingClientRect();
        if (rect.top <= 1 && rect.bottom > bottom) bottom = rect.bottom;
      });
      return Math.max(0, bottom);
    }

    overlayOpen(rect) {
      const bodyStyle = global.getComputedStyle(global.document.body);
      const rootStyle = global.getComputedStyle(global.document.documentElement);
      if (['hidden', 'clip'].includes(bodyStyle.overflowY) || ['hidden', 'clip'].includes(rootStyle.overflowY)) return true;
      const overlays = global.document.querySelectorAll(
        'cart-drawer.active, cart-notification.active, dialog[open], details[open] .menu-drawer, ' +
        '.product-media-modal[open], .product-popup-modal[open], [role="dialog"][aria-modal="true"], ' +
        '[data-cdp-purchase-obstruction]'
      );
      if (Array.from(overlays).some((overlay) => this.visible(overlay))) return true;
      const active = global.document.activeElement;
      if (active && active !== global.document.body && !this.contains(active) && this.visible(active)) {
        if (intersects(active.getBoundingClientRect(), rect)) return true;
      }
      return Array.from(global.document.querySelectorAll('iframe')).some((frame) =>
        this.visible(frame) && intersects(frame.getBoundingClientRect(), rect)
      );
    }

    syncPrice(source) {
      const shown = source && !source.classList.contains('hidden') && !source.hidden;
      const signature = shown ? source.innerHTML : '';
      if (signature === this.priceSignature) return;
      this.priceSignature = signature;
      this.price.replaceChildren();
      if (!shown) return;
      Array.from(source.children).forEach((child) => {
        const clone = child.cloneNode(true);
        [clone, ...clone.querySelectorAll('*')].forEach((node) => {
          ['id', 'aria-live', 'aria-labelledby', 'aria-describedby', 'role'].forEach((attribute) => node.removeAttribute(attribute));
        });
        clone.querySelectorAll('script, input, button, a, select, textarea').forEach((node) => node.remove());
        this.price.append(clone);
      });
    }

    sync() {
      const state = readPurchaseState(this.product, this.dataset.sectionId);
      this.button.disabled = state.blocked;
      this.button.setAttribute('aria-disabled', String(state.blocked));
      this.button.setAttribute('aria-busy', String(state.loading));
      const popup = state.submit?.getAttribute('aria-haspopup');
      if (popup) this.button.setAttribute('aria-haspopup', popup);
      else this.button.removeAttribute('aria-haspopup');
      if (state.label && this.label.textContent !== state.label) this.label.textContent = state.label;
      const parts = [];
      if (state.selection?.title) parts.push(state.selection.title);
      if (state.quantity) parts.push(`${this.dataset.quantityLabel}: ${state.quantity.value}`);
      const selectionText = parts.join(' · ');
      if (this.selection.textContent !== selectionText) this.selection.textContent = selectionText;
      this.syncPrice(state.price);
      const viewport = global.visualViewport;
      const height = viewport?.height || global.innerHeight;
      const width = viewport?.width || global.innerWidth;
      const top = this.headerBottom();
      if (!state.ready || !this.visible(state.submit) || viewport?.scale > 1.05 ||
          (viewport && global.innerHeight - viewport.height > 150)) {
        this.setVisible(false);
        return;
      }
      const nativeRect = state.submit.getBoundingClientRect();
      if (nativeRect.bottom > top) {
        this.setVisible(false);
        return;
      }
      this.style.setProperty('--cdp-purchase-top', `${top}px`);
      this.hidden = false;
      const dockHeight = this.getBoundingClientRect().height;
      const dockRect = { top, bottom: top + dockHeight, left: 0, right: width };
      const sectionRect = this.product.getBoundingClientRect();
      const withinProduct = sectionRect.top < height && sectionRect.bottom > dockRect.bottom + 24;
      const fits = dockHeight > 0 && top + dockHeight < height * 0.4;
      const visible = withinProduct && fits && !this.overlayOpen(dockRect);
      this.setVisible(visible, dockRect.bottom + 16);
    }

    setVisible(visible, clearance = 0) {
      if (!visible && this.contains(global.document.activeElement)) {
        const source = readPurchaseState(this.product, this.dataset.sectionId);
        source.submit?.focus({ preventScroll: true });
      }
      this.hidden = !visible;
      global.document.documentElement.classList.toggle('cdp-purchase-visible', visible);
      if (visible) global.document.documentElement.style.setProperty('--cdp-purchase-clearance', `${clearance}px`);
      else global.document.documentElement.style.removeProperty('--cdp-purchase-clearance');
    }

    editNative(target) {
      const state = readPurchaseState(this.product, this.dataset.sectionId);
      const input = target || this.product.querySelector('variant-selects input:checked, variant-selects select') ||
        state.quantity || state.submit;
      this.setVisible(false);
      input?.scrollIntoView({ block: 'center', behavior: 'instant' });
      input?.focus({ preventScroll: true });
    }

    activate(event) {
      event.preventDefault();
      const state = readPurchaseState(this.product, this.dataset.sectionId);
      if (state.blocked || this.forwarding) {
        this.sync();
        return;
      }
      if (state.quantity && !state.quantity.checkValidity()) {
        this.editNative(state.quantity);
        state.quantity.reportValidity();
        return;
      }
      this.forwarding = true;
      this.button.disabled = true;
      try {
        state.submit.focus({ preventScroll: true });
        state.submit.click();
      } finally {
        this.forwarding = false;
        this.sync();
      }
    }
  }

  if (!global.customElements.get('dink-sticky-purchase')) {
    global.customElements.define('dink-sticky-purchase', DinkStickyPurchase);
  }
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { readPurchaseState, intersects, DinkStickyPurchase };
  }
})(typeof window === 'undefined' ? globalThis : window);
